import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'karting-timing-highlighted-team-ids';

interface UseHighlightedTeamsResult {
  highlightedTeamIds: Set<string>;
  toggleHighlight: (teamId: string) => void;
  clearHighlights: () => void;
  isHighlighted: (teamId: string) => boolean;
}

function readFromStorage(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(
      parsed.filter(
        (x): x is string => typeof x === 'string' && x.trim().length > 0,
      ),
    );
  } catch (err) {
    console.error('Error loading highlighted teams from localStorage:', err);
    return new Set();
  }
}

export const useHighlightedTeams = (): UseHighlightedTeamsResult => {
  const [highlightedTeamIds, setHighlightedTeamIds] = useState<Set<string>>(
    () => readFromStorage(),
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(highlightedTeamIds)),
      );
    } catch (err) {
      console.error('Error saving highlighted teams to localStorage:', err);
    }
  }, [highlightedTeamIds]);

  const toggleHighlight = useCallback((teamId: string) => {
    if (!teamId) return;
    setHighlightedTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  }, []);

  const clearHighlights = useCallback(() => {
    setHighlightedTeamIds(new Set());
  }, []);

  const isHighlighted = useCallback(
    (teamId: string): boolean =>
      teamId ? highlightedTeamIds.has(teamId) : false,
    [highlightedTeamIds],
  );

  return {
    highlightedTeamIds,
    toggleHighlight,
    clearHighlights,
    isHighlighted,
  };
};
